export type PrivacyShortcut = string;
export type PrivacyKeyBinding = {
  code: string;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
  metaKey: boolean;
};
type PrivacyKeyEvent = PrivacyKeyBinding & {
  repeat: boolean;
  isComposing: boolean;
  getModifierState?: (key: string) => boolean;
};
const modifierNames = ['Ctrl', 'Alt', 'Shift', 'Meta'] as const;
const keyNames: Record<string, string> = {
  Backquote: '`',
  Space: '空格',
  Minus: '-',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  Comma: ',',
  Period: '.',
  Slash: '/',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
};
export function parsePrivacyShortcut(value: unknown): PrivacyKeyBinding | null {
  if (typeof value !== 'string' || value.length > 80) return null;
  const parts = value.split('+'),
    code = parts.pop() || '';
  if (
    parts.some((p) => !modifierNames.includes(p as (typeof modifierNames)[number])) ||
    new Set(parts).size !== parts.length
  )
    return null;
  if (
    !/^(?:Key[A-Z]|Digit[0-9]|F(?:[1-9]|1[0-9]|2[0-4])|Backquote|Space|Minus|Equal|BracketLeft|BracketRight|Backslash|Semicolon|Quote|Comma|Period|Slash|Insert|Home|End|PageUp|PageDown|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Pause)$/.test(
      code,
    )
  )
    return null;
  const binding = {
    code,
    ctrlKey: parts.includes('Ctrl'),
    altKey: parts.includes('Alt'),
    shiftKey: parts.includes('Shift'),
    metaKey: parts.includes('Meta'),
  };
  // Reserve reload, navigation, developer tools and common browser/OS commands.
  if (
    /^F(?:1|3|5|6|7|10|11|12)$/.test(code) ||
    (code === 'F4' && (binding.ctrlKey || binding.altKey || binding.metaKey))
  )
    return null;
  if (
    (binding.ctrlKey || binding.metaKey) &&
    !binding.altKey &&
    /^(?:Key[ACDFHIJKLNOPQRSTUWY]|Digit[0-9]|Space|BracketLeft|BracketRight|ArrowLeft|ArrowRight|Home|End)$/.test(
      code,
    )
  )
    return null;
  if (
    binding.altKey &&
    !binding.ctrlKey &&
    !binding.metaKey &&
    /^(?:ArrowLeft|ArrowRight|Home|Space)$/.test(code)
  )
    return null;
  return binding;
}
export function shortcutFromEvent(event: PrivacyKeyEvent): string | null {
  if (event.repeat || event.isComposing || event.getModifierState?.('AltGraph')) return null;
  const value = [
    event.ctrlKey && 'Ctrl',
    event.altKey && 'Alt',
    event.shiftKey && 'Shift',
    event.metaKey && 'Meta',
    event.code,
  ]
    .filter(Boolean)
    .join('+');
  return parsePrivacyShortcut(value) ? value : null;
}
export function normalizePrivacyShortcut(value: unknown): string | null {
  const binding = parsePrivacyShortcut(value);
  return binding ? shortcutFromEvent({ ...binding, repeat: false, isComposing: false }) : null;
}
export const WEBSITE_SHORTCUTS = [
  {
    title: '百度搜索',
    url: 'https://www.baidu.com/',
    domain: 'baidu.com',
    tabTitle: '百度一下，你就知道',
    icon: '/privacy-icons/baidu.ico',
  },
  {
    title: '360 搜索',
    url: 'https://www.so.com/',
    domain: 'so.com',
    tabTitle: '360搜索，SO靠谱',
    icon: '/privacy-icons/360.ico',
  },
  {
    title: '菜鸟教程',
    url: 'https://www.runoob.com/',
    domain: 'runoob.com',
    tabTitle: '菜鸟教程',
    icon: '/privacy-icons/runoob.ico',
  },
  {
    title: 'W3School',
    url: 'https://www.w3school.com.cn/',
    domain: 'w3school.com.cn',
    tabTitle: 'W3School 在线教程',
    icon: '/privacy-icons/w3school.png',
  },
] as const;
export const COVER_PAGES = [
  { id: 'document', title: '项目文档', description: '工作记录，可直接编辑' },
  { id: 'sheet', title: '数据表格', description: '项目明细与进度汇总' },
  { id: 'board', title: '工作看板', description: '任务分栏，一目了然' },
  { id: 'custom', title: '自定义网页', description: '填写你常用的网站地址' },
] as const;
export type CoverPage = (typeof COVER_PAGES)[number]['id'];
export type TabIdentity = { title: string; icon: string };
export type WebsiteTab = TabIdentity & { url: string };
declare global {
  interface Window {
    MoyuPrivacyTab?: { cover: (tab: TabIdentity) => void; restore: () => void };
  }
}
export type PrivacySettings = {
  shortcut: PrivacyShortcut;
  mode: 'notes' | 'webpage';
  page: CoverPage;
  url: string;
  tab?: WebsiteTab;
  autoHideToolbar?: boolean;
};
export const PRIVACY_SETTINGS_KEY = 'moyu:privacy:settings:v1';
export const PRIVACY_HIDDEN_KEY = 'moyu:privacy:hidden:v1';
export const PRIVACY_EVENT = 'moyu:privacy-change';
export const DEFAULT_PRIVACY: PrivacySettings = {
  shortcut: 'F8',
  mode: 'notes',
  page: 'document',
  url: '',
};
export const shortcutLabel = (key: PrivacyShortcut) =>
  key
    .split('+')
    .map((part) =>
      part === 'Meta' ? '⌘ / Win' : keyNames[part] || part.replace(/^(Key|Digit)/, ''),
    )
    .join(' + ');
export function safeCoverUrl(value: string): string | null {
  try {
    const url = new URL(value.trim());
    return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password
      ? url.href
      : null;
  } catch {
    return null;
  }
}
export function safeTabIcon(value: string): string | null {
  if (/^\/privacy-icons\/[a-z0-9.-]+$/.test(value) || value === '/work-note.svg') return value;
  const url = safeCoverUrl(value);
  return url?.startsWith('https:') ? url : null;
}
export function parseWebsiteTab(value: unknown, target: string): WebsiteTab | null {
  if (!value || typeof value !== 'object') return null;
  const tab = value as Record<string, unknown>;
  if (typeof tab.url !== 'string' || typeof tab.title !== 'string' || typeof tab.icon !== 'string')
    return null;
  const url = safeCoverUrl(tab.url),
    icon = safeTabIcon(tab.icon);
  const title = tab.title
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .trim()
    .slice(0, 160);
  return url && url === safeCoverUrl(target) && title && icon ? { url, title, icon } : null;
}
export function privacyTabIdentity(settings: PrivacySettings, notesFallback = false): TabIdentity {
  if (settings.mode === 'notes' || notesFallback)
    return { title: '工作笔记', icon: '/work-note.svg' };
  if (settings.page !== 'custom')
    return {
      title: { document: '项目计划与会议记录', sheet: '项目进度汇总', board: '本周工作安排' }[
        settings.page
      ],
      icon: '/work-note.svg',
    };
  const url = safeCoverUrl(settings.url);
  const cached = parseWebsiteTab(settings.tab, settings.url);
  if (cached) return cached;
  const preset = WEBSITE_SHORTCUTS.find((site) => site.url === url);
  if (preset) return { title: preset.tabTitle, icon: preset.icon };
  if (!url) return { title: '工作网页', icon: '/work-note.svg' };
  const parsed = new URL(url);
  return {
    title: parsed.hostname,
    icon: parsed.protocol === 'https:' ? new URL('/favicon.ico', parsed).href : '/work-note.svg',
  };
}
export async function fetchWebsiteTab(url: string, signal: AbortSignal): Promise<WebsiteTab> {
  const response = await fetch('/api/privacy/metadata', {
    method: 'POST',
    signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
  });
  if (!response.ok) throw new Error('暂时无法读取网站信息');
  const tab = parseWebsiteTab(await response.json(), url);
  if (!tab) throw new Error('网站信息不完整');
  return tab;
}
export function parsePrivacySettings(value: string | null): PrivacySettings {
  try {
    const raw = JSON.parse(value || 'null');
    if (!raw || typeof raw !== 'object') return { ...DEFAULT_PRIVACY };
    const url = typeof raw.url === 'string' ? safeCoverUrl(raw.url) || '' : '';
    // Existing URL-only preferences always remain custom webpages.
    const page: CoverPage = COVER_PAGES.some((item) => item.id === raw.page)
      ? raw.page
      : url
        ? 'custom'
        : 'document';
    const coverValid =
      raw.mode === 'redirect' ? !!url : raw.page ? page !== 'custom' || !!url : !!url;
    const tab = parseWebsiteTab(raw.tab, url);
    return {
      shortcut: normalizePrivacyShortcut(raw.shortcut) || 'F8',
      ...(typeof raw.autoHideToolbar === 'boolean' ? { autoHideToolbar: raw.autoHideToolbar } : {}),
      // Migrate the old navigation setting without ever leaving the current document.
      mode: ['redirect', 'webpage'].includes(raw.mode) && coverValid ? 'webpage' : 'notes',
      page: raw.mode === 'redirect' && url ? 'custom' : page,
      url,
      ...(tab ? { tab } : {}),
    };
  } catch {
    return { ...DEFAULT_PRIVACY };
  }
}
export function matchesPrivacyKey(e: PrivacyKeyEvent, key: PrivacyShortcut, editing = false) {
  const binding = parsePrivacyShortcut(key);
  return (
    !!binding &&
    !e.repeat &&
    !e.isComposing &&
    !e.getModifierState?.('AltGraph') &&
    e.code === binding.code &&
    ['ctrlKey', 'altKey', 'shiftKey', 'metaKey'].every(
      (flag) => !!e[flag as keyof PrivacyKeyBinding] === binding[flag as keyof PrivacyKeyBinding],
    ) &&
    !(
      editing &&
      !binding.ctrlKey &&
      !binding.altKey &&
      !binding.metaKey &&
      !/^F[0-9]+$/.test(binding.code)
    )
  );
}
export const privacyHidden = () => document.documentElement.dataset.privacy === 'hidden';
